import { BarChart2, Mail, Pause, Pencil, Play, Send, Trash2, Workflow } from 'lucide-react';
import { formatDate, formatTime } from '@/shared/lib/format';
import type { Campaign, CampaignListMode, MetabaseCampaign } from '@/shared/types/campaign';
import { Badge } from '@/shared/ui/badge';
import { channelLabel } from '@/features/marketing-shared/model/channels';
import { ActionIconButton } from '@/shared/components/action-icon-button';
import { SelectableCard } from '@/shared/components/selectable-card';
import {
  campaignDispatchMeta,
  formatCampaignCreatedAt,
} from '../model/campaign-dates';
import {
  automatedReachStatus,
  campaignDisplayDateTime,
  manualReachStatus,
  uploadReachStatus,
} from '../model/campaign-status';

const MODE_ICON = {
  manual: Mail,
  automated: Workflow,
  upload: Send,
} as const;

interface ManualAutomatedCardProps {
  campaign: Campaign;
  mode: 'manual' | 'automated';
  selected: boolean;
  onToggle: () => void;
  index: number;
  onRemove?: () => void;
  onEdit?: () => void;
  onPause?: () => void;
  onResults?: () => void;
}

interface UploadCardProps {
  campaign: MetabaseCampaign;
  selected: boolean;
  onToggle: () => void;
  index: number;
}

export function CampaignCard({
  campaign,
  mode,
  selected,
  onToggle,
  index,
  onRemove,
  onEdit,
  onPause,
  onResults,
}: ManualAutomatedCardProps) {
  const Icon = MODE_ICON[mode];
  const message = campaign.description ?? campaign.message ?? '—';
  const shortMsg = message.length > 80 ? `${message.slice(0, 80)}…` : message;
  const showResults =
    mode === 'automated' && (campaign.expirar === true || campaign.frequencia === '31');

  if (mode === 'automated') {
    const created = formatCampaignCreatedAt(campaign.created_at ?? campaign.updated);
    const dispatch = campaignDispatchMeta(campaign);
    const reach = automatedReachStatus(campaign.received ?? campaign.total_recebidos);
    const channel = channelLabel(campaign.tipo_disparo);

    return (
      <SelectableCard
        selected={selected}
        onToggle={onToggle}
        index={index}
        ariaLabel={`Selecionar campanha ${campaign.name ?? ''}`}
        actions={
          <div className="flex items-center gap-1">
            {onEdit ? (
              <ActionIconButton label="Editar horário e mensagem" onClick={onEdit}>
                <Pencil className="size-4" />
              </ActionIconButton>
            ) : null}
            {onPause ? (
              <ActionIconButton
                label={campaign.active === false ? 'Ativar campanha' : 'Pausar campanha'}
                onClick={onPause}
              >
                {campaign.active === false ? <Play className="size-4" /> : <Pause className="size-4" />}
              </ActionIconButton>
            ) : null}
            {onRemove ? (
              <ActionIconButton label="Excluir campanha" onClick={onRemove} className="text-destructive">
                <Trash2 className="size-4" />
              </ActionIconButton>
            ) : null}
            {showResults && onResults ? (
              <ActionIconButton label="Ver resultados" onClick={onResults}>
                <BarChart2 className="size-4" />
              </ActionIconButton>
            ) : null}
          </div>
        }
      >
        <div className="flex items-center gap-3">
          <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-primary/12 text-primary">
            <Icon className="size-5" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <p className="truncate font-medium">{campaign.name || 'Campanha'}</p>
              {created.date ? (
                <span className="text-xs text-muted-foreground tabular-nums">
                  {created.date}
                  {created.time ? ` ${created.time}` : ''}
                </span>
              ) : null}
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              {dispatch.dateLabel ? (
                <Badge variant="secondary" className="font-normal tabular-nums">
                  {dispatch.dateLabel}
                </Badge>
              ) : null}
              {dispatch.timeLabel ? (
                <Badge variant="secondary" className="font-normal tabular-nums">
                  {dispatch.timeLabel}
                </Badge>
              ) : null}
              <Badge variant={reach.variant} className="font-normal">
                Alcance: {reach.label}
              </Badge>
              <Badge variant={campaign.active === false ? 'secondary' : 'success'} className="font-normal">
                {campaign.active === false ? 'Inativo' : 'Ativo'}
              </Badge>
              {channel ? (
                <Badge variant="outline" className="font-normal">
                  {channel}
                </Badge>
              ) : null}
            </div>
            <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{shortMsg}</p>
          </div>
        </div>
      </SelectableCard>
    );
  }

  const { date, time } = campaignDisplayDateTime(mode, campaign);
  const reach = manualReachStatus(campaign.received);

  return (
    <SelectableCard
      selected={selected}
      onToggle={onToggle}
      index={index}
      ariaLabel={`Selecionar campanha ${campaign.name ?? ''}`}
      actions={
        <div className="flex items-center gap-1">
          {onRemove ? (
            <ActionIconButton label="Remover campanha" onClick={onRemove} className="text-destructive">
              <Trash2 className="size-4" />
            </ActionIconButton>
          ) : null}
        </div>
      }
    >
      <div className="flex items-center gap-3">
        <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-primary/12 text-primary">
          <Icon className="size-5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate font-medium">{campaign.name || 'Campanha'}</p>
            {date ? (
              <span className="text-xs text-muted-foreground tabular-nums">
                {formatDate(date)}
                {time ? ` · ${time.length <= 5 ? time : formatTime(date)}` : ''}
              </span>
            ) : null}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <Badge variant={reach.variant} className="font-normal">
              Alcance: {reach.label}
            </Badge>
          </div>
          <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{shortMsg}</p>
        </div>
      </div>
    </SelectableCard>
  );
}

export function UploadCampaignCard({ campaign, selected, onToggle, index }: UploadCardProps) {
  const message = campaign.description ?? '—';
  const shortMsg = message.length > 80 ? `${message.slice(0, 80)}…` : message;
  const { date, time } = campaignDisplayDateTime('upload', campaign);
  const reach = uploadReachStatus(campaign.received);
  const audience = campaign.users?.length ?? 0;

  return (
    <SelectableCard
      selected={selected}
      onToggle={onToggle}
      index={index}
      ariaLabel={`Selecionar campanha ${campaign.name ?? ''}`}
    >
      <div className="flex items-center gap-3">
        <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-primary/12 text-primary">
          <Send className="size-5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate font-medium">{campaign.name || 'Campanha'}</p>
            {date ? (
              <span className="text-xs text-muted-foreground tabular-nums">
                {formatDate(date)}
                {time ? ` · ${time}` : ''}
              </span>
            ) : null}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <Badge variant="secondary" className="font-normal tabular-nums">
              Público: {audience}
            </Badge>
            <Badge variant={reach.variant} className="font-normal">
              Alcance: {reach.label}
            </Badge>
          </div>
          <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{shortMsg}</p>
        </div>
      </div>
    </SelectableCard>
  );
}

export type { CampaignListMode };
